import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import { Layout } from '../components/Layout';
import { Orb } from '../components/Orb';
import { BoardCard } from '../components/BoardCard';
import { CardDrawer } from '../components/CardDrawer';
import { api } from '../lib/api';
import { midpoint } from '@shared/board';
import type { AgentId, BoardData, Card, CardColumnKey, CardWorkType, Me, Tag } from '@shared/types';
import { CARD_WORK_TYPES } from '@shared/types';
import { AGENT_COLORS, AGENT_LABELS, AGENT_ORDER, todayYmd } from '../lib/boardMeta';
import '../board.css';

/**
 * The Work Board (/board) — every piece of agent work as a card moving through
 * the six-column pipeline (BOARD-SPEC + the v2 addendum). Quinn's QA column is
 * the mint gate; Human review is the amber "needs me" column. Founders drag
 * cards anywhere (hand-rolled pointer DnD — no deps, now with the §D tilt:
 * the ghost leans ~4° into its horizontal velocity and springs upright on
 * drop), filter by agent AND tags, reveal archived cards, quick-add into any
 * column, flip to a Calendar of due dates, and open any card into the v2
 * drawer.
 */

/* ---------------------------------------------------------------- copy */

const EMPTY_COPY: Record<CardColumnKey, string> = {
  backlog: 'Nothing queued. Add a card and its agent will pick it up.',
  in_progress: 'Nothing being built right now.',
  qa: "Quinn's desk is clear.",
  human_review: 'Nothing waiting on you.',
  scheduled_live: 'Nothing scheduled or live yet.',
  done: 'Finished work lands here, receipts included.',
};

const WORK_TYPE_OPTIONS: Array<{ value: CardWorkType; label: string }> = [
  { value: 'campaign', label: 'Email campaign' },
  { value: 'flow', label: 'Email flow' },
  { value: 'cro_test', label: 'CRO test' },
  { value: 'page', label: 'Landing page' },
  { value: 'creative', label: 'Creative' },
  { value: 'other', label: 'Something else' },
];

function ShieldIcon() {
  return (
    <svg width="10" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 2l8 4v6c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V6l8-4z" />
    </svg>
  );
}

function reducedMotion(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/* ------------------------------------------------------------ calendar */

interface CalCell {
  ymd: string;
  day: number;
  inMonth: boolean;
}

function ymdOf(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Sunday-start month grid; trailing all-out-of-month weeks trimmed. */
function buildMonth(y: number, m: number): CalCell[] {
  const first = new Date(y, m, 1);
  const start = new Date(y, m, 1 - first.getDay());
  const cells: CalCell[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
    cells.push({ ymd: ymdOf(d), day: d.getDate(), inMonth: d.getMonth() === m });
  }
  while (cells.length > 7 && cells.slice(-7).every((c) => !c.inMonth)) cells.splice(-7, 7);
  return cells;
}

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function CalPill({
  card,
  ghost,
  onPointerDown,
  onOpen,
}: {
  card: Card;
  ghost?: boolean;
  onPointerDown?: (e: ReactPointerEvent<HTMLDivElement>) => void;
  onOpen?: () => void;
}) {
  return (
    <div
      className={`cal-pill${card.column_key === 'done' ? ' is-done' : ''}`}
      role={ghost ? undefined : 'button'}
      tabIndex={ghost ? -1 : 0}
      onPointerDown={ghost ? undefined : onPointerDown}
      onKeyDown={
        ghost || !onOpen
          ? undefined
          : (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onOpen();
              }
            }
      }
      aria-label={ghost ? undefined : `Open card: ${card.title}`}
      title={card.title}
    >
      <span className="adot" style={{ background: AGENT_COLORS[card.agent_id] ?? 'var(--ice)' }} aria-hidden />
      <span className="cal-pill-title">{card.title}</span>
    </div>
  );
}

/* ------------------------------------------------------------ add card */

function AddCardForm({
  column,
  columnLabel,
  onAdded,
  onCancel,
}: {
  column: CardColumnKey;
  columnLabel: string;
  onAdded: () => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState('');
  const [agent, setAgent] = useState<AgentId>('milo');
  const [workType, setWorkType] = useState<CardWorkType>('campaign');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!title.trim() || busy) return;
    setBusy(true);
    try {
      await api.post('/board/cards', {
        title: title.trim(),
        agent_id: agent,
        work_type: workType,
        notes: notes.trim() || undefined,
        column_key: column,
      });
      onAdded();
    } catch (e) {
      window.alert(`Couldn't add the card${e instanceof Error ? ` — ${e.message}` : ''}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="addcard">
      <input
        className="field"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="What needs doing?"
        aria-label="Card title"
        autoFocus
        onKeyDown={(e) => {
          if (e.key === 'Enter') void submit();
        }}
      />
      <div style={{ display: 'flex', gap: 8 }}>
        <select className="field" value={agent} onChange={(e) => setAgent(e.target.value as AgentId)} aria-label="Which agent">
          {AGENT_ORDER.map((a) => (
            <option key={a} value={a}>
              {AGENT_LABELS[a]}
            </option>
          ))}
        </select>
        <select
          className="field"
          value={workType}
          onChange={(e) => setWorkType(e.target.value as CardWorkType)}
          aria-label="Kind of work"
        >
          {WORK_TYPE_OPTIONS.filter((o) => (CARD_WORK_TYPES as readonly string[]).includes(o.value)).map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
      <textarea
        className="field"
        style={{ minHeight: 52, resize: 'vertical' }}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Any notes for them? (optional)"
        aria-label="Notes"
      />
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" className="btn btn-mint sm" disabled={busy || !title.trim()} onClick={() => void submit()}>
          Add to {columnLabel}
        </button>
        <button type="button" className="btn btn-ghost sm" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------- tag filter */

function TagFilter({
  tags,
  selected,
  onToggle,
  onClear,
}: {
  tags: Tag[];
  selected: Set<string>;
  onToggle: (id: string) => void;
  onClear: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [open]);

  if (tags.length === 0) return null;

  return (
    <div className="tagfilter" ref={ref}>
      <button
        type="button"
        className={`fpill${selected.size > 0 ? ' active' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        Tags{selected.size > 0 ? ` · ${selected.size}` : ' ▾'}
      </button>
      {open && (
        <div className="mmenu-pop tagpop" role="menu" style={{ left: 0, right: 'auto' }}>
          {tags.map((t) => (
            <button
              key={t.id}
              type="button"
              role="menuitemcheckbox"
              aria-checked={selected.has(t.id)}
              onClick={() => onToggle(t.id)}
            >
              <span className="tdot" style={{ background: t.color }} aria-hidden />
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.name}</span>
              {typeof t.count === 'number' && t.count > 0 && <span className="tcount">{t.count}</span>}
              {selected.has(t.id) && <span aria-hidden>✓</span>}
            </button>
          ))}
          {selected.size > 0 && (
            <button type="button" role="menuitem" onClick={onClear} style={{ color: 'var(--dim)' }}>
              Clear tag filter
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/* --------------------------------------------------------------- page */

interface PendingDrag {
  card: Card;
  mode: 'board' | 'calendar';
  pointerType: string;
  startX: number;
  startY: number;
  lastX: number;
  lastY: number;
  lastT: number;
  /** Smoothed horizontal velocity (px/ms) — drives the §D tilt. */
  vx: number;
  offsetX: number;
  offsetY: number;
  width: number;
  height: number;
  started: boolean;
  holdTimer: number | null;
}

interface DragState {
  card: Card;
  mode: 'board' | 'calendar';
  width: number;
  height: number;
}

interface DropTarget {
  col: CardColumnKey;
  index: number;
}

export default function Board() {
  const [data, setData] = useState<BoardData | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [actionErr, setActionErr] = useState<string | null>(null);
  const [me, setMe] = useState<Me | null>(null);
  const [allTags, setAllTags] = useState<Tag[]>([]);

  const [view, setView] = useState<'board' | 'calendar'>('board');
  const [agentFilter, setAgentFilter] = useState<AgentId | 'all'>('all');
  const [tagFilter, setTagFilter] = useState<Set<string>>(() => new Set());
  const [showArchived, setShowArchived] = useState(false);
  const [needsMe, setNeedsMe] = useState(false);
  const [adding, setAdding] = useState<CardColumnKey | null>(null);
  const [drawerId, setDrawerId] = useState<string | null>(null);

  const [drag, setDrag] = useState<DragState | null>(null);
  /** The ghost is springing upright after a drop — the landed card is already back in its column. */
  const [settling, setSettling] = useState(false);
  const [drop, setDrop] = useState<DropTarget | null>(null);
  const [dayDrop, setDayDrop] = useState<string | null>(null);

  const [cal, setCal] = useState(() => {
    const n = new Date();
    return { y: n.getFullYear(), m: n.getMonth() };
  });

  // Refs mirror the state the window-level pointer handlers need.
  const dataRef = useRef<BoardData | null>(null);
  dataRef.current = data;
  const agentFilterRef = useRef(agentFilter);
  agentFilterRef.current = agentFilter;
  const tagFilterRef = useRef(tagFilter);
  tagFilterRef.current = tagFilter;
  const pendingRef = useRef<PendingDrag | null>(null);
  const dropRef = useRef<DropTarget | null>(null);
  const dayDropRef = useRef<string | null>(null);
  const ghostRef = useRef<HTMLDivElement>(null);
  const colsRef = useRef<HTMLDivElement>(null);
  const settleTimerRef = useRef<number | null>(null);

  /* ------------------------------------------------------------- data */

  const load = useCallback(async () => {
    try {
      const [d, tags] = await Promise.all([
        api.get<BoardData>(`/board${showArchived ? '?archived=1' : ''}`),
        api.get<Tag[]>('/board/tags').catch(() => null),
      ]);
      setData(d);
      if (tags) setAllTags(tags);
      setLoadErr(null);
      setActionErr(null);
    } catch (e) {
      setLoadErr(e instanceof Error ? e.message : String(e));
    }
  }, [showArchived]);

  useEffect(() => {
    void load();
    const t = setInterval(() => {
      // Never yank cards out from under a drag in progress.
      if (!pendingRef.current && !document.hidden) void load();
    }, 15000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    void api.get<Me>('/auth/me').then(setMe).catch(() => {});
  }, []);

  const byFilters = useCallback(
    (c: Card) => {
      if (agentFilter !== 'all' && c.agent_id !== agentFilter) return false;
      if (tagFilter.size > 0 && !(c.tags ?? []).some((t) => tagFilter.has(t.id))) return false;
      return true;
    },
    [agentFilter, tagFilter],
  );

  /** The cards a founder currently SEES in a column (drag math must match). */
  const visibleIn = useCallback((d: BoardData, col: CardColumnKey): Card[] => {
    const agent = agentFilterRef.current;
    const tagIds = tagFilterRef.current;
    const cards = d.columns.find((c) => c.key === col)?.cards ?? [];
    return cards.filter((c) => {
      if (agent !== 'all' && c.agent_id !== agent) return false;
      if (tagIds.size > 0 && !(c.tags ?? []).some((t) => tagIds.has(t.id))) return false;
      return true;
    });
  }, []);

  const applyLocalMove = useCallback((cardId: string, col: CardColumnKey, sortOrder: number) => {
    setData((prev) => {
      if (!prev) return prev;
      let moved: Card | undefined;
      const stripped = prev.columns.map((c) => ({
        ...c,
        cards: c.cards.filter((k) => {
          if (k.id === cardId) {
            moved = k;
            return false;
          }
          return true;
        }),
      }));
      if (!moved) return prev;
      const updated: Card = { ...moved, column_key: col, sort_order: sortOrder, moved_at: new Date().toISOString() };
      return {
        columns: stripped.map((c) =>
          c.key === col
            ? {
                ...c,
                cards: [...c.cards, updated].sort(
                  (a, b) => a.sort_order - b.sort_order || a.created_at.localeCompare(b.created_at),
                ),
              }
            : c,
        ),
      };
    });
  }, []);

  const patchCard = useCallback(
    (cardId: string, body: Record<string, unknown>, failMsg: string) => {
      api
        .patch(`/board/cards/${cardId}`, body)
        .then(() => load())
        .catch((e: unknown) => {
          setActionErr(`${failMsg}${e instanceof Error ? ` — ${e.message}` : ''}`);
          void load();
        });
    },
    [load],
  );

  /* --------------------------------------------------- archive / delete */

  const cardAction = useCallback(
    (run: () => Promise<unknown>, failMsg: string) => {
      run()
        .then(() => load())
        .catch((e: unknown) => {
          setActionErr(`${failMsg}${e instanceof Error ? ` — ${e.message}` : ''}`);
          void load();
        });
    },
    [load],
  );

  const archiveCard = useCallback(
    (card: Card) => cardAction(() => api.patch(`/board/cards/${card.id}/archive`), "Couldn't archive the card"),
    [cardAction],
  );
  const restoreCard = useCallback(
    (card: Card) => cardAction(() => api.patch(`/board/cards/${card.id}/restore`), "Couldn't bring the card back"),
    [cardAction],
  );
  const deleteCard = useCallback(
    (card: Card) => {
      cardAction(() => api.del(`/board/cards/${card.id}`, { confirm: card.id }), "Couldn't delete the card");
      setDrawerId((cur) => (cur === card.id ? null : cur));
    },
    [cardAction],
  );

  /* ------------------------------------------------------ drag engine */

  const setDropTarget = useCallback((v: DropTarget | null) => {
    const cur = dropRef.current;
    if ((v === null && cur === null) || (v !== null && cur !== null && v.col === cur.col && v.index === cur.index)) return;
    dropRef.current = v;
    setDrop(v);
  }, []);

  const setDayTarget = useCallback((v: string | null) => {
    if (dayDropRef.current === v) return;
    dayDropRef.current = v;
    setDayDrop(v);
  }, []);

  const positionGhost = useCallback((x: number, y: number) => {
    const p = pendingRef.current;
    const el = ghostRef.current;
    if (!p || !el) return;
    // §D — the tilt: lean up to ~4° into the horizontal velocity, scale 1.03.
    // Reduced motion (or a calendar pill): no tilt, no scale — just follow.
    const tilt = p.mode === 'board' && !reducedMotion();
    const deg = tilt ? Math.max(-4, Math.min(4, p.vx * 8)) : 0;
    const scale = tilt ? ' scale(1.03)' : '';
    el.style.transform = `translate(${x - p.offsetX}px, ${y - p.offsetY}px) rotate(${deg.toFixed(2)}deg)${scale}`;
  }, []);

  const updateTarget = useCallback(
    (x: number, y: number) => {
      const p = pendingRef.current;
      if (!p?.started) return;
      const el = document.elementFromPoint(x, y);
      if (p.mode === 'calendar') {
        const dayEl = el?.closest<HTMLElement>('[data-day]');
        setDayTarget(dayEl ? dayEl.dataset.day ?? null : null);
        return;
      }
      const colEl = el?.closest<HTMLElement>('[data-col]');
      if (!colEl) {
        setDropTarget(null);
        return;
      }
      const col = colEl.dataset.col as CardColumnKey;
      // Rendered cards exclude the one being dragged, so DOM order = the
      // insertion list; the drop slot carries no data-card-id.
      const cardEls = Array.from(colEl.querySelectorAll<HTMLElement>('[data-card-id]'));
      let index = cardEls.length;
      for (let i = 0; i < cardEls.length; i++) {
        const r = cardEls[i].getBoundingClientRect();
        if (y < r.top + r.height / 2) {
          index = i;
          break;
        }
      }
      setDropTarget({ col, index });
    },
    [setDayTarget, setDropTarget],
  );

  const startDrag = useCallback(
    (p: PendingDrag) => {
      p.started = true;
      if (p.holdTimer !== null) {
        window.clearTimeout(p.holdTimer);
        p.holdTimer = null;
      }
      // A new drag interrupts a settling ghost from the previous one.
      if (settleTimerRef.current !== null) {
        window.clearTimeout(settleTimerRef.current);
        settleTimerRef.current = null;
      }
      ghostRef.current?.classList.remove('settling');
      setSettling(false);
      setDrag({ card: p.card, mode: p.mode, width: p.width, height: p.height });
      document.body.classList.add('board-dragging');
      updateTarget(p.lastX, p.lastY);
    },
    [updateTarget],
  );

  const commitBoardDrop = useCallback(
    (card: Card) => {
      const target = dropRef.current;
      dropRef.current = null;
      if (!target) return;
      const d = dataRef.current;
      if (!d) return;

      const insertList = visibleIn(d, target.col).filter((c) => c.id !== card.id);
      if (target.col === card.column_key) {
        // The card's index in the full visible list equals its slot in the
        // excluded list (everything before it is unchanged) — dropping back
        // into that slot is a no-op.
        const cur = visibleIn(d, target.col).findIndex((c) => c.id === card.id);
        if (cur !== -1 && cur === target.index) return;
      }
      // Midpoint between the two visible neighbours — with a filter on, hidden
      // cards may interleave, but the visible order the founder set holds.
      const before = target.index > 0 ? insertList[target.index - 1].sort_order : null;
      const after = target.index < insertList.length ? insertList[target.index].sort_order : null;
      const sortOrder = midpoint(before, after);

      applyLocalMove(card.id, target.col, sortOrder);
      patchCard(card.id, { column_key: target.col, sort_order: sortOrder }, "Couldn't move the card");
    },
    [applyLocalMove, patchCard, visibleIn],
  );

  const commitCalendarDrop = useCallback(
    (card: Card) => {
      const day = dayDropRef.current;
      dayDropRef.current = null;
      if (day === null) return;
      const due = day === 'none' ? null : day;
      if ((card.due_date ?? null) === due) return;
      setData((prev) =>
        prev
          ? {
              columns: prev.columns.map((c) => ({
                ...c,
                cards: c.cards.map((k) => (k.id === card.id ? { ...k, due_date: due } : k)),
              })),
            }
          : prev,
      );
      patchCard(card.id, { due_date: due }, "Couldn't set the date");
    },
    [patchCard],
  );

  // ONE window-level engine, attached once; every handler reads the refs.
  useEffect(() => {
    const preventTouch = (e: TouchEvent) => e.preventDefault();

    const teardown = () => {
      const p = pendingRef.current;
      if (p?.holdTimer != null) window.clearTimeout(p.holdTimer);
      window.removeEventListener('touchmove', preventTouch);
      document.body.classList.remove('board-dragging');
    };

    const onMove = (e: PointerEvent) => {
      const p = pendingRef.current;
      if (!p) return;
      // Smoothed horizontal velocity for the tilt (px per ms, EMA).
      const now = performance.now();
      const dt = now - p.lastT;
      if (dt > 0) {
        const inst = (e.clientX - p.lastX) / dt;
        p.vx = p.vx * 0.8 + inst * 0.2;
        p.lastT = now;
      }
      p.lastX = e.clientX;
      p.lastY = e.clientY;
      if (!p.started) {
        const dist = Math.hypot(e.clientX - p.startX, e.clientY - p.startY);
        if (p.pointerType === 'touch') {
          // Moved before the hold fired → the founder is scrolling, not dragging.
          if (dist > 8) {
            if (p.holdTimer !== null) window.clearTimeout(p.holdTimer);
            pendingRef.current = null;
          }
          return;
        }
        if (dist < 6) return;
        startDrag(p); // mouse/pen: threshold reached (touch starts via the hold timer)
      }
      positionGhost(e.clientX, e.clientY);
      updateTarget(e.clientX, e.clientY);
      // Nudge the horizontal scroller when dragging near its edges.
      if (p.mode === 'board' && colsRef.current) {
        const r = colsRef.current.getBoundingClientRect();
        if (e.clientX < r.left + 48) colsRef.current.scrollLeft -= 12;
        else if (e.clientX > r.right - 48) colsRef.current.scrollLeft += 12;
      }
    };

    const onUp = () => {
      const p = pendingRef.current;
      teardown();
      pendingRef.current = null;
      if (!p) return;
      if (!p.started) {
        // A clean click — open the drawer.
        setDrawerId(p.card.id);
        return;
      }
      setDrop(null);
      setDayDrop(null);
      if (p.mode === 'board') commitBoardDrop(p.card);
      else commitCalendarDrop(p.card);
      dropRef.current = null;
      dayDropRef.current = null;

      // §D — spring upright on drop (~180ms overshoot), then let go. Reduced
      // motion: the ghost just vanishes and the card is already in place.
      const el = ghostRef.current;
      if (el && p.mode === 'board' && !reducedMotion()) {
        el.classList.add('settling');
        el.style.transform = `translate(${p.lastX - p.offsetX}px, ${p.lastY - p.offsetY}px) rotate(0deg) scale(1)`;
        setSettling(true); // the landed card renders in its column while the ghost melts away
        settleTimerRef.current = window.setTimeout(() => {
          settleTimerRef.current = null;
          setSettling(false);
          setDrag(null);
        }, 190);
      } else {
        setDrag(null);
      }
    };

    const onCancel = () => {
      teardown();
      pendingRef.current = null;
      dropRef.current = null;
      dayDropRef.current = null;
      setDrag(null);
      setSettling(false);
      setDrop(null);
      setDayDrop(null);
    };

    // Expose the hold-timer start path (touch) to the pointerdown handler.
    touchStartDragRef.current = (p: PendingDrag) => {
      startDrag(p);
      window.addEventListener('touchmove', preventTouch, { passive: false });
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
      if (settleTimerRef.current !== null) window.clearTimeout(settleTimerRef.current);
      teardown();
    };
  }, [commitBoardDrop, commitCalendarDrop, positionGhost, startDrag, updateTarget]);

  const touchStartDragRef = useRef<(p: PendingDrag) => void>(() => {});

  const onCardPointerDown = useCallback((e: ReactPointerEvent<HTMLElement>, card: Card, mode: 'board' | 'calendar') => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    if (pendingRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const p: PendingDrag = {
      card,
      mode,
      pointerType: e.pointerType,
      startX: e.clientX,
      startY: e.clientY,
      lastX: e.clientX,
      lastY: e.clientY,
      lastT: performance.now(),
      vx: 0,
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
      width: rect.width,
      height: rect.height,
      started: false,
      holdTimer: null,
    };
    pendingRef.current = p;
    if (e.pointerType === 'touch') {
      // Touch: a short hold starts the drag; moving first means scrolling.
      p.holdTimer = window.setTimeout(() => {
        const q = pendingRef.current;
        if (q === p && !q.started) touchStartDragRef.current(q);
      }, 220);
    }
  }, []);

  // Place the ghost the instant it mounts (before first pointermove repaint).
  useLayoutEffect(() => {
    if (!drag) return;
    const p = pendingRef.current;
    if (p) positionGhost(p.lastX, p.lastY);
  }, [drag, positionGhost]);

  /* ---------------------------------------------------------- derived */

  const allCards = useMemo(() => (data ? data.columns.flatMap((c) => c.cards) : []), [data]);
  const drawerCard = useMemo(
    () => (drawerId ? allCards.find((c) => c.id === drawerId) ?? null : null),
    [drawerId, allCards],
  );
  // Card vanished from the board (rare) — don't strand an empty drawer.
  useEffect(() => {
    if (drawerId && data && !drawerCard) setDrawerId(null);
  }, [drawerId, data, drawerCard]);

  // "Needs me" is action, not history — archived cards never count, even when
  // "Show archived" is revealing them in the lanes.
  const needsMeCards = useMemo(
    () =>
      data
        ? (data.columns.find((c) => c.key === 'human_review')?.cards ?? []).filter((c) => byFilters(c) && !c.archived_at)
        : [],
    [data, byFilters],
  );
  const needsCount = needsMeCards.length;
  const totalCards = allCards.length;

  const today = todayYmd();
  const calCells = useMemo(() => buildMonth(cal.y, cal.m), [cal]);
  const monthLabel = new Date(cal.y, cal.m, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const filteredCards = useMemo(() => allCards.filter(byFilters), [allCards, byFilters]);
  // The calendar plans live work — archived cards stay out of it entirely
  // (they surface on the board lanes via "Show archived", with Restore).
  const cardsByDay = useMemo(() => {
    const map = new Map<string, Card[]>();
    for (const c of filteredCards) {
      if (!c.due_date || c.archived_at) continue;
      const list = map.get(c.due_date) ?? [];
      list.push(c);
      map.set(c.due_date, list);
    }
    return map;
  }, [filteredCards]);
  const undated = useMemo(
    () => filteredCards.filter((c) => !c.due_date && c.column_key !== 'done' && !c.archived_at),
    [filteredCards],
  );

  /* --------------------------------------------------------- rendering */

  const headerActions = (
    <div className="toggle" role="group" aria-label="Board or calendar view">
      <button className={view === 'board' ? 'active' : ''} onClick={() => setView('board')}>
        BOARD
      </button>
      <button className={view === 'calendar' ? 'active' : ''} onClick={() => setView('calendar')}>
        CALENDAR
      </button>
    </div>
  );

  function boardCardFor(c: Card) {
    return (
      <BoardCard
        key={c.id}
        card={c}
        onPointerDown={(e) => onCardPointerDown(e, c, 'board')}
        onOpen={() => setDrawerId(c.id)}
        onArchive={() => archiveCard(c)}
        onRestore={() => restoreCard(c)}
        onDelete={() => deleteCard(c)}
      />
    );
  }

  function renderColumn(col: { key: CardColumnKey; label: string; cards: Card[] }) {
    let cards = col.cards.filter(byFilters);
    if (drag && !settling) cards = cards.filter((c) => c.id !== drag.card.id);
    const slotHere = drag && drop?.col === col.key;
    const rendered: JSX.Element[] = cards.map((c) => boardCardFor(c));
    if (slotHere && drag) {
      rendered.splice(
        Math.min(drop.index, rendered.length),
        0,
        <div key="drop-slot" className="drop-slot" style={{ height: drag.height }} aria-hidden />,
      );
    }
    return (
      <section
        key={col.key}
        data-col={col.key}
        className={`board-col${col.key === 'qa' ? ' col-qa' : ''}${col.key === 'human_review' ? ' col-human' : ''}${
          slotHere ? ' drop-hot' : ''
        }`}
        aria-label={`${col.label} column`}
      >
        <header className="board-col-head">
          <span className="t-label">{col.label}</span>
          {col.key === 'qa' && (
            <span className="chip chip-mint">
              <ShieldIcon />
              gate
            </span>
          )}
          {col.key === 'human_review' && (
            <span className="chip chip-amber">
              <i />
              you
            </span>
          )}
          <span className="col-count">{cards.length}</span>
          <button
            className="addbtn"
            onClick={() => setAdding((cur) => (cur === col.key ? null : col.key))}
            aria-label={`Add a card to ${col.label}`}
            title={`Add a card to ${col.label}`}
          >
            +
          </button>
        </header>
        {adding === col.key && (
          <AddCardForm
            column={col.key}
            columnLabel={col.label}
            onAdded={() => {
              setAdding(null);
              void load();
            }}
            onCancel={() => setAdding(null)}
          />
        )}
        <div className="board-col-body">
          {rendered}
          {rendered.length === 0 && <p className="board-empty">{EMPTY_COPY[col.key]}</p>}
        </div>
      </section>
    );
  }

  return (
    <Layout title="Work Board" actions={headerActions}>
      <div className="board-wrap">
        {/* filters */}
        <div className="fpills" role="group" aria-label="Filter the board">
          <button className={`fpill${agentFilter === 'all' ? ' active' : ''}`} onClick={() => setAgentFilter('all')}>
            All
          </button>
          {AGENT_ORDER.map((a) => (
            <button
              key={a}
              className={`fpill${agentFilter === a ? ' active' : ''}`}
              onClick={() => setAgentFilter((cur) => (cur === a ? 'all' : a))}
            >
              <span className="adot" style={{ background: AGENT_COLORS[a] }} aria-hidden />
              {AGENT_LABELS[a]}
            </button>
          ))}
          <TagFilter
            tags={allTags}
            selected={tagFilter}
            onToggle={(id) =>
              setTagFilter((cur) => {
                const next = new Set(cur);
                if (next.has(id)) next.delete(id);
                else next.add(id);
                return next;
              })
            }
            onClear={() => setTagFilter(new Set())}
          />
          <span style={{ flex: 1 }} />
          <button
            className={`fpill${showArchived ? ' active' : ''}`}
            onClick={() => setShowArchived((v) => !v)}
            aria-pressed={showArchived}
            title="Archived cards are tucked away, not gone — flip this to see them"
          >
            Show archived
          </button>
          <button
            className={`fpill needsme${needsMe ? ' active' : ''}`}
            onClick={() => setNeedsMe((v) => !v)}
            aria-pressed={needsMe}
          >
            Needs me{needsCount > 0 ? ` · ${needsCount}` : ''}
          </button>
        </div>

        {actionErr && (
          <p className="small" style={{ color: 'var(--coral)' }}>
            {actionErr} — the board reloaded itself, nothing was lost.
          </p>
        )}

        {data === null && loadErr && (
          <div className="card">
            <p style={{ color: 'var(--coral)' }}>Couldn't load the board — {loadErr}</p>
            <button className="btn btn-line" style={{ marginTop: 12 }} onClick={() => void load()}>
              Retry
            </button>
          </div>
        )}
        {data === null && !loadErr && <p className="small">Loading the board…</p>}

        {data !== null && needsMe && (
          <>
            <h2 style={{ fontSize: 18 }}>Waiting on you</h2>
            {needsCount === 0 ? (
              <div className="card" style={{ textAlign: 'center', padding: '44px 20px' }}>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
                  <Orb agent="quinn" size="md" />
                </div>
                <p className="dim">Nothing needs you right now — Quinn is holding the gate.</p>
              </div>
            ) : (
              <div className="needsme-grid">{needsMeCards.map((c) => boardCardFor(c))}</div>
            )}
          </>
        )}

        {data !== null && !needsMe && view === 'board' && (
          <>
            {totalCards === 0 && (
              <div className="card" style={{ textAlign: 'center', padding: '36px 20px' }}>
                <p className="dim">
                  The board is empty. Cards appear here the moment an agent picks up work — or add one to the backlog
                  yourself with the + button.
                </p>
              </div>
            )}
            <div className="board-cols" ref={colsRef}>
              {data.columns.map((col) => renderColumn(col))}
            </div>
          </>
        )}

        {data !== null && !needsMe && view === 'calendar' && (
          <>
            <div className="cal-head">
              <button
                className="btn btn-line sm"
                onClick={() => setCal((c) => ({ y: c.m === 0 ? c.y - 1 : c.y, m: c.m === 0 ? 11 : c.m - 1 }))}
                aria-label="Previous month"
              >
                ‹
              </button>
              <span className="cal-month">{monthLabel}</span>
              <button
                className="btn btn-line sm"
                onClick={() => setCal((c) => ({ y: c.m === 11 ? c.y + 1 : c.y, m: c.m === 11 ? 0 : c.m + 1 }))}
                aria-label="Next month"
              >
                ›
              </button>
              <button
                className="btn btn-ghost sm"
                onClick={() => {
                  const n = new Date();
                  setCal({ y: n.getFullYear(), m: n.getMonth() });
                }}
              >
                Today
              </button>
            </div>

            <div className={`cal-tray${dayDrop === 'none' ? ' drop-hot' : ''}`} data-day="none">
              <span className="cal-tray-label">
                {undated.length > 0
                  ? 'No date yet — drag a card onto a day to schedule it (or drop one here to unschedule):'
                  : 'Everything in flight has a date. Drop a card here to take its date off.'}
              </span>
              {undated
                .filter((c) => !drag || c.id !== drag.card.id)
                .map((c) => (
                  <CalPill
                    key={c.id}
                    card={c}
                    onPointerDown={(e) => onCardPointerDown(e, c, 'calendar')}
                    onOpen={() => setDrawerId(c.id)}
                  />
                ))}
            </div>

            <div className="cal-scroll">
              <div className="cal-grid" style={{ marginBottom: 2 }}>
                {DOW.map((d) => (
                  <div key={d} className="cal-dow">
                    {d}
                  </div>
                ))}
              </div>
              <div className="cal-grid">
                {calCells.map((cell) => (
                  <div
                    key={cell.ymd}
                    data-day={cell.ymd}
                    className={`cal-day${cell.inMonth ? '' : ' other'}${cell.ymd === today ? ' today' : ''}${
                      dayDrop === cell.ymd ? ' drop-hot' : ''
                    }`}
                  >
                    <div className="dnum">{cell.day}</div>
                    {(cardsByDay.get(cell.ymd) ?? [])
                      .filter((c) => !drag || c.id !== drag.card.id)
                      .map((c) => (
                        <CalPill
                          key={c.id}
                          card={c}
                          onPointerDown={(e) => onCardPointerDown(e, c, 'calendar')}
                          onOpen={() => setDrawerId(c.id)}
                        />
                      ))}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      {/* the drag ghost — follows the pointer, never intercepts it */}
      {drag && (
        <div className="board-ghost" ref={ghostRef} style={{ width: drag.width }} aria-hidden>
          {drag.mode === 'board' ? <BoardCard card={drag.card} ghost /> : <CalPill card={drag.card} ghost />}
        </div>
      )}

      {drawerCard && (
        <CardDrawer
          card={drawerCard}
          me={me}
          onClose={() => setDrawerId(null)}
          onChanged={() => void load()}
        />
      )}
    </Layout>
  );
}
